import {
  matchIdentity,
  mergeMatchCollections,
  mergeMatchSnapshots,
  normalizeMatchSnapshot,
  isCompletedSet,
} from '../data/matchState.js';
import { canonicalTeamSlug } from '../data/teamIdentity.js';
import { fetchApiJson } from './apiRequest.js';
import { getGameFlow } from './gameFlow.js';

const LEDGER_PREFIX = 'volleywire.observedGames.v2';
const MAX_LEDGER_GAMES = 240;
const DETAIL_BATCH_SIZE = 8;
const FINAL_DETAIL_CACHE_MS = 5 * 60 * 1000;
const LIVE_DETAIL_CACHE_MS = 8 * 1000;
const detailCache = new Map();
const recoveryAttempts = new Map();

export function publishMatchSnapshot(snapshot, date) {
  const id = gameId(snapshot);
  if (!id) return;
  const merged = mergeMatchSnapshots(detailCache.get(id)?.game, snapshot);
  detailCache.set(id, { game: merged, expiresAt: Date.now() + (hasCompleteSetScores(merged) ? FINAL_DETAIL_CACHE_MS : LIVE_DETAIL_CACHE_MS) });
  if (date) writeLedger(date, mergeMatchCollections(readLedger(date), [merged]));
}

function storageKey(date) {
  return `${LEDGER_PREFIX}:${date}`;
}

function gameId(game) {
  const value = String(game?.gameId || game?.id || '').trim();
  return /^[1-9]\d*$/.test(value) ? value : '';
}

export function mergeObservedGames(previous = [], incoming = []) {
  const reconciledIncoming = incoming.map((game) => {
    const duplicate = previous.find((candidate) => likelyDuplicate(candidate, game));
    if (!duplicate) return game;
    const merged = mergeMatchSnapshots(duplicate, game);
    return {
      ...merged,
      id: game.id,
      gameId: game.gameId,
      gameUrl: game.gameUrl,
      startTimeIso: game.startTimeIso,
      startTimeEpoch: game.startTimeEpoch,
      timeLabel: game.timeLabel,
      timeConfidence: game.timeConfidence,
    };
  });
  const incomingKeys = new Set(reconciledIncoming.map(matchIdentity).filter(Boolean));
  const retainedPrevious = previous.filter((game) =>
    !reconciledIncoming.some((candidate) => likelyDuplicate(game, candidate))
  );

  return mergeMatchCollections(retainedPrevious, reconciledIncoming).map((game) => ({
    ...game,
    observedRetained: !incomingKeys.has(matchIdentity(game)) || Boolean(game?.observedRetained),
  }));
}

export { mergeMatchSnapshots, normalizeMatchSnapshot };

function readLedger(date) {
  if (typeof window === 'undefined') return [];
  try {
    const value = JSON.parse(window.localStorage.getItem(storageKey(date)) || '[]');
    return Array.isArray(value) ? value.slice(0, MAX_LEDGER_GAMES) : [];
  } catch {
    return [];
  }
}

function writeLedger(date, games) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      storageKey(date),
      JSON.stringify(games.slice(0, MAX_LEDGER_GAMES))
    );
  } catch {
    // Scores remain usable when storage is unavailable or full.
  }
}

export async function getScores({ date, sport, signal }) {
  const observed = readLedger(date);
  const observedIds = [...observed]
    .sort((left, right) => {
      const priority = (game) =>
        game?.status === 'live' ? 0 : game?.status === 'scheduled' ? 1 : 2;
      return priority(left) - priority(right);
    })
    .map(gameId)
    .filter(Boolean);
  const params = new URLSearchParams({
    date,
    sport,
  });

  if (observedIds.length) {
    params.set('observed', observedIds.slice(0, 20).join(','));
  }

  let response, payload;
  try { ({ response, payload } = await fetchApiJson(`/api/scores?${params.toString()}`, {
    signal,
    headers: {
      Accept: 'application/json',
    },
  })); } catch (error) {
    if (!signal?.aborted && observed.length) return { games: readLedger(date), stale: true };
    throw error;
  }

  if (!response.ok) {
    if (observed.length) return { games: observed, stale: true };
    throw new Error(
      response.status === 503
        ? 'Scores are temporarily unavailable.'
        : 'Unable to load scores.'
    );
  }

  // Match Center may publish a newer snapshot while this request is in flight.
  const games = mergeObservedGames(readLedger(date), Array.isArray(payload?.games) ? payload.games : []);
  writeLedger(date, games);
  return { ...payload, games };
}

export function hasCompleteSetScores(game) {
  const away = game?.away?.setScores || [];
  const home = game?.home?.setScores || [];
  const expected = Number(game?.away?.score) + Number(game?.home?.score);
  if (game?.status !== 'final' || !Number.isInteger(expected) || expected < 3 || expected > 5) return false;
  return Array.from({ length: expected }, (_, i) => i).every((i) => isCompletedSet(away[i], home[i], i + 1));
}

export function needsScoreRecovery(game) {
  if (game?.status === 'final') return !hasCompleteSetScores(game);
  if (game?.status !== 'live') return false;
  const current = Number(normalizeMatchSnapshot(game).currentSet) || 1;
  return Array.from({ length: current }, (_, i) => i).some((i) =>
    !Number.isFinite(game?.away?.setScores?.[i]) || !Number.isFinite(game?.home?.setScores?.[i]));
}

export async function getScoreDetails({ date, sport = 'women', games = [], signal, onProgress }) {
  const candidates = games.filter((game) => {
    const id = gameId(game);
    if (!id) return false;
    return game?.status === 'live' || (['final', 'suspended', 'unconfirmed'].includes(game?.status) && !hasCompleteSetScores(game));
  });
  candidates.sort((a, b) => Number(b.status === 'live') - Number(a.status === 'live'));
  const uniqueIds = Array.from(new Set(candidates.map(gameId).filter(Boolean)));
  const originals = new Map(candidates.map((game) => [gameId(game), game]));
  const now = Date.now();
  const output = [];
  const missingIds = [];

  uniqueIds.forEach((id) => {
    const cached = detailCache.get(id);
    if (cached && cached.expiresAt > now) {
      if (cached.game) output.push(cached.game);
      return;
    }
    missingIds.push(id);
  });

  if (output.length && !signal?.aborted) onProgress?.([...output]);
  const recovery = new Map();
  const remember = (id, game) => {
    if (signal?.aborted) throw signal.reason;
    detailCache.set(id, { game, expiresAt: Date.now() + (hasCompleteSetScores(game) ? FINAL_DETAIL_CACHE_MS : LIVE_DETAIL_CACHE_MS) });
    output.push(game);
    onProgress?.([game]);
  };
  let nextBatch = 0;
  async function worker() {
    while (nextBatch < missingIds.length) {
    if (signal?.aborted) throw signal.reason;
    const cursor = nextBatch;
    nextBatch += DETAIL_BATCH_SIZE;
    const ids = missingIds.slice(cursor, cursor + DETAIL_BATCH_SIZE);
    const params = new URLSearchParams({
      date,
      sport,
      details: ids.join(','),
    });
    let response, payload;
    try {
      ({ response, payload } = await fetchApiJson(`/api/scores?${params.toString()}`, {
        signal, headers: { Accept: 'application/json' }, timeoutMs: 12000,
      }));
    } catch (error) {
      if (signal?.aborted) throw error;
      // A summary outage should not prevent the rally-backed fallback.
      ids.forEach((id) => { if (needsScoreRecovery(originals.get(id))) recovery.set(id, originals.get(id)); });
      continue;
    }

    if (!response.ok) {
      ids.forEach((id) => { if (needsScoreRecovery(originals.get(id))) recovery.set(id, originals.get(id)); });
      continue;
    }
    const returnedGames = Array.isArray(payload?.games) ? payload.games : [];
    const byId = new Map(returnedGames.map((game) => [gameId(game), game]));

    for (const id of ids) {
      let game = mergeMatchSnapshots(originals.get(id), detailCache.get(id)?.game || originals.get(id));
      if (byId.has(id)) game = mergeMatchSnapshots(game, byId.get(id));
      remember(id, game);
      if (needsScoreRecovery(game)) recovery.set(id, game);
    }
    }
  }
  await Promise.all(Array.from({ length: Math.min(2, Math.ceil(missingIds.length / DETAIL_BATCH_SIZE)) }, worker));
  // Publish every summary batch first. Slow/missing rally feeds must not block
  // unrelated cards. Only two recovery requests run at a time.
  const recoveryQueue = [...recovery]
    .filter(([id]) => Date.now() - (recoveryAttempts.get(id) || 0) > 15000)
    .sort(([a, left], [b, right]) => Number(right.status === 'live') - Number(left.status === 'live') ||
      (recoveryAttempts.get(a) || 0) - (recoveryAttempts.get(b) || 0))
    .slice(0, 4);
  let nextRecovery = 0;
  await Promise.all(Array.from({ length: Math.min(2, recoveryQueue.length) }, async () => {
    while (nextRecovery < recoveryQueue.length) {
      if (signal?.aborted) throw signal.reason;
      const [id, game] = recoveryQueue[nextRecovery++];
      recoveryAttempts.set(id, Date.now());
      while (recoveryAttempts.size > 240) recoveryAttempts.delete(recoveryAttempts.keys().next().value);
      try {
        const flow = await getGameFlow({ gameId: id, game, live: game.status === 'live', signal, timeoutMs: 8000 });
        if (flow.match) remember(id, mergeMatchSnapshots(game, flow.match));
      } catch (error) {
        if (signal?.aborted) throw error;
      }
    }
  }));

  return mergeMatchCollections([], output);
}

function matchupKey(game) {
  const teams = [game?.away, game?.home]
    .map((team) => canonicalTeamSlug(team?.slug || team?.name))
    .filter(Boolean)
    .sort();
  return teams.length === 2 ? teams.join('|') : '';
}

function isPlaceholderStart(game) {
  return /^(?:12:00|1:00)\s+AM\b/i.test(String(game?.timeLabel || '').trim());
}

function resultSignature(game) {
  if (game?.status !== 'final') return '';
  const rows = [game?.away, game?.home]
    .map((team) => ({
      key: canonicalTeamSlug(team?.slug || team?.name),
      score: Number(team?.score),
    }))
    .filter((row) => row.key && Number.isFinite(row.score))
    .sort((left, right) => left.key.localeCompare(right.key));
  return rows.length === 2
    ? rows.map((row) => `${row.key}:${row.score}`).join('|')
    : '';
}

function likelyDuplicate(left, right) {
  if (!left || !right || matchupKey(left) !== matchupKey(right)) return false;
  const leftId = gameId(left);
  const rightId = gameId(right);
  if (!leftId || !rightId || leftId === rightId) return false;
  const matchingFinal = resultSignature(left) && resultSignature(left) === resultSignature(right);
  const advancedReplacement =
    (left?.status === 'scheduled' && ['live', 'final'].includes(right?.status)) ||
    (right?.status === 'scheduled' && ['live', 'final'].includes(left?.status));
  return (isPlaceholderStart(left) || isPlaceholderStart(right)) &&
    (matchingFinal || advancedReplacement);
}
